import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Bookings.scss";
import {
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { Table, Button, Input, DatePicker } from "antd";
import { getApi } from "../../Utlis/apiService";
import {
  GET_ALL_CLASSESS_COUNT,
  GET_ALL_CLASSESS_COUNT_PAST,
  GET_ALL_CLASSESS_COUNT_CURRENT,
  GET_ALL_CLASSESS_COUNT_OFFLINE,
  GET_ALL_CLASSESS_COUNT_OFFLINE_PAST,
  GET_ALL_CLASSESS_COUNT_OFFLINE_CURRENT,
  GET_ALL_BOOKINGS,
  GET_ALL_BOOKINGS_OFFLINE,
} from "../../Utlis/apiPath";

const { RangePicker } = DatePicker;

const Bookings = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [classCount, setClassCount] = useState({
    totalBookedClasses: 0,
    groupClasses: 0,
    oneOnOneClasses: 0,
    rescheduledClasses: 0,
    premiumClasses: 0,
  });
  const [dateRange, setDateRange] = useState([]);
  const [isOnline, setIsOnline] = useState(true);

  const [offlineClassCount, setOfflineClassCount] = useState({
    totalBookings: 0,
    alternateBookings: 0,
    customBookings: 0,
  });
  const [offlineTableData, setOfflineTableData] = useState([]);
  const [isAscending, setIsAscending] = useState(true);

  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // unified fetch function instead of too many scatterred functions
  const fetchBookingsData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        pageIndex: pagination.current - 1,
        pageSize: pagination.pageSize,
        isAscending,
        search: debouncedSearch,
        ...(dateRange[0] &&
          dateRange[1] && {
            startDate: dateRange[0].format("YYYY-MM-DD"),
            endDate: dateRange[1].format("YYYY-MM-DD"),
          }),
      };

      let endpoint;
      if (isOnline) {
        switch (activeTab) {
          case "1":
            endpoint = GET_ALL_BOOKINGS;
            break;
          case "2":
            endpoint = GET_ALL_CLASSESS_COUNT_PAST;
            break;
          case "3":
            endpoint = GET_ALL_CLASSESS_COUNT_CURRENT;
            break;
          default:
            endpoint = GET_ALL_BOOKINGS;
        }
      } else {
        switch (activeTab) {
          case "1":
            endpoint = GET_ALL_BOOKINGS_OFFLINE;
            break;
          case "2":
            endpoint = GET_ALL_CLASSESS_COUNT_OFFLINE_PAST;
            break;
          case "3":
            endpoint = GET_ALL_CLASSESS_COUNT_OFFLINE_CURRENT;
            break;
          default:
            endpoint = GET_ALL_BOOKINGS_OFFLINE;
        }
      }

      const { status, data } = await getApi(endpoint, { params });

      if (status === 200) {
        if (isOnline) {
          const formattedData = data.bookings.map((item) => ({
            ...item,
            subscriptionStartDate: item.subscriptionStartDate?.split("T")[0],
            subscriptionEndDate: item.subscriptionEndDate?.split("T")[0],
            bookedOn: item.bookedOn?.split("T")[0],
          }));
          setTableData(formattedData);
        } else {
          setOfflineTableData(data.bookings);
        }

        setPagination((prev) => ({
          ...prev,
          total: data.totalRecords,
          current: Math.min(
            prev.current,
            Math.ceil(data.totalRecords / prev.pageSize) || 1
          ),
        }));
      } else {
        if (isOnline) {
          setTableData([]);
        } else {
          setOfflineTableData([]);
        }
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      if (isOnline) {
        setTableData([]);
      } else {
        setOfflineTableData([]);
      }
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoading(false);
    }
  }, [
    isOnline,
    debouncedSearch,
    pagination.current,
    pagination.pageSize,
    dateRange,
    activeTab,
    isAscending,
  ]);

  useEffect(() => {
    fetchBookingsData();
  }, [fetchBookingsData]);

  useEffect(() => {
    if (isOnline) fetchAllClassCount();
    else fetchAllClassCountOffline();
  }, [isOnline]);

  const handleSorting = () => {
    setIsAscending((prevState) => !prevState);
    // Reset to first page when sorting changes
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const fetchAllClassCount = async () => {
    setIsLoading(true);
    try {
      const { status, data } = await getApi(GET_ALL_CLASSESS_COUNT);
      if (status === 200) {
        setClassCount(data);
      } else {
        setClassCount({
          totalBookedClasses: 0,
          groupClasses: 0,
          oneOnOneClasses: 0,
          rescheduledClasses: 0,
          premiumClasses: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClassCount({
        totalBookedClasses: 0,
        groupClasses: 0,
        oneOnOneClasses: 0,
        rescheduledClasses: 0,
        premiumClasses: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllClassCountOffline = async () => {
    setIsLoading(true);
    try {
      const { status, data } = await getApi(GET_ALL_CLASSESS_COUNT_OFFLINE);
      if (status === 200) {
        setOfflineClassCount(data);
      } else {
        setOfflineClassCount({
          totalBookings: 0,
          alternateBookings: 0,
          customBookings: 0,
        });
      }
    } catch (error) {
      setOfflineClassCount({
        totalBookings: 0,
        alternateBookings: 0,
        customBookings: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableChange = (tablePagination) => {
    setPagination((prev) => ({
      ...prev,
      current: tablePagination.current,
      pageSize: tablePagination.pageSize,
    }));
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);

    if (value === "" && debouncedSearch !== "") {
      setDebouncedSearch("");
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || []);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleOnlineToggle = () => {
    setIsOnline(!isOnline);
    setActiveTab("1");
    setSearch("");
    setDebouncedSearch("");
    setPagination({ current: 1, pageSize: 10, total: 0 });
  };

  const columns = [
    {
      title: "SL No",
      key: "slNo",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + (index + 1),
    },
    { title: "Class Details", dataIndex: "classTitle" },
    {
      title: "Student Name",
      dataIndex: "studentName",
    },
    { title: "Instructor", dataIndex: "instructorName" },
    { title: "Class Type", dataIndex: "classType" },
    { title: "Subscription Type", dataIndex: "subscriptionType" },
    { title: "Start Date", dataIndex: "subscriptionStartDate" },
    { title: "End Date", dataIndex: "subscriptionEndDate" },
    { title: "Booking Date", dataIndex: "bookedOn" },
    { title: "Amount", dataIndex: "amountPaid" },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <span
          style={{
            color:
              status === "ongoing"
                ? "orange"
                : status === "Cancelled"
                ? "#f5222d"
                : "#1890ff",
          }}
        >
          {status}
        </span>
      ),
    },
  ];

  const offlineColumns = [
    {
      title: "SL No",
      key: "slNo",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + (index + 1),
    },
    { title: "Student Name", dataIndex: "StudentName" },
    {
      title: "Instructor Name",
      dataIndex: "instructor",
      render: (text) => text.name,
    },
    { title: "Subscription Type", dataIndex: "subscriptionType" },
    { title: "Slot Pattern", dataIndex: "slotPattern" },
    { title: "Slot Time", dataIndex: "slotTime" },
    { title: "Start Date", dataIndex: "startDate" },
    { title: "End Date", dataIndex: "endDate" },
    { title: "Amount", dataIndex: "amount" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span
          style={{
            color: status === "upcoming" ? "green" : "orange",
            fontWeight: "bold",
          }}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div className="booking">
      {/* Toggle Online or Offline tab*/}
      <div className="booking_header">
        <h2>Bookings</h2>
        <div className="online-toggle">
          <div className="tab-navigation">
            <button
              className={`tab-button ${isOnline ? "active" : ""}`}
              onClick={() => {
                if (!isOnline) {
                  setIsOnline(true);
                  setActiveTab("1");
                  setSearch("");
                  setDebouncedSearch("");
                  setPagination({ current: 1, pageSize: 10, total: 0 });
                }
              }}
            >
              Online
            </button>

            <button
              className={`tab-button ${!isOnline ? "active" : ""}`}
              onClick={() => {
                if (isOnline) {
                  setIsOnline(false);
                  setActiveTab("1");
                  setSearch("");
                  setDebouncedSearch("");
                  setPagination({ current: 1, pageSize: 10, total: 0 });
                }
              }}
            >
              Offline
            </button>
          </div>
        </div>
      </div>

      <div className="booking_sec">
        <div className="booking_sec_card">
          {isOnline ? (
            <>
              <div className="booking_sec_card_data">
                <div className="content">
                  <h3>Total Bookings</h3>
                  <h2>{classCount.totalBookedClasses}</h2>
                </div>
              </div>
              <div className="booking_sec_card_data">
                <div className="content">
                  <h3>Group Classes</h3>
                  <h2>{classCount.groupClasses}</h2>
                </div>
              </div>
              <div className="booking_sec_card_data">
                <div className="content">
                  <h3>One-on-One Classes</h3>
                  <h2>{classCount.oneOnOneClasses}</h2>
                </div>
              </div>
              <div className="booking_sec_card_data">
                <div className="content">
                  <h3>Premium Classes</h3>
                  <h2>{classCount.premiumClasses}</h2>
                </div>
              </div>
              <div className="booking_sec_card_data">
                <div className="content">
                  <h3>Rescheduled Class</h3>
                  <h2>{classCount.rescheduledClasses}</h2>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="booking_sec_card_data">
                <div className="content">
                  <h3>Total Bookings</h3>
                  <h2>{offlineClassCount.totalBookings}</h2>
                </div>
              </div>
              <div className="booking_sec_card_data">
                <div className="content">
                  <h3>Alternate Bookings</h3>
                  <h2>{offlineClassCount.alternateBookings}</h2>
                </div>
              </div>
              <div className="booking_sec_card_data">
                <div className="content">
                  <h3>Custom Bookings</h3>
                  <h2>{offlineClassCount.customBookings}</h2>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="booking_header">
        <h2>List of all classes</h2>
        <RangePicker onChange={handleDateChange} value={dateRange} allowClear />
      </div>

      <div className="booking_content">
        <div className="booking_content_custom">
          <div
            className={activeTab === "1" ? "tab selected" : "tab"}
            onClick={() => handleTabChange("1")}
          >
            All
          </div>
          <div
            className={activeTab === "2" ? "tab selected" : "tab"}
            onClick={() => handleTabChange("2")}
          >
            Past
          </div>
          <div
            className={activeTab === "3" ? "tab selected" : "tab"}
            onClick={() => handleTabChange("3")}
          >
            Current
          </div>
        </div>

        <div className="booking_content_search">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={search}
            onChange={handleSearch}
          />
          <Button
            icon={
              isAscending ? (
                <SortAscendingOutlined />
              ) : (
                <SortDescendingOutlined />
              )
            }
            className="sortButton"
            onClick={handleSorting}
          />
        </div>
      </div>

      <Table
        rowKey={(record, index) => record.id || index}
        columns={isOnline ? columns : offlineColumns}
        dataSource={isOnline ? tableData : offlineTableData}
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default Bookings;
